import { ILabeledField, RawFilters } from "operations/search-filter-operation";
import { SingleOrArray, UnionIntersect } from "types/utility";
import { QuickAccess } from "utils/constants";

export type BaseAmenities =
  "access"
  | "area_pool"
  | "areatennis"
  | "corner"
  | "culdesac"
  | "elevator"
  | "energy"
  | "garageapt"
  | "greenbelt"
  | "greencert"
  | "ingolfcom"
  | "lake"
  | "mediarm"
  | "ongolfcourse"
  | "patiodeck"
  // | "pool"
  | "spahottub"
  | "sprinkle"
  | "studyrm"
  | "waterfront"
  | "waterview"
  | "wheelchair"
  | "wooded"
  | "yard"
  | "loft"
  | "furnished";

export type HarAmenities = BaseAmenities
  | "private_pool";

export type ToolFilterAmenities = HarAmenities
  | "no_private_pool";

export type IHarAmenities = {
  [key in HarAmenities]?: string;
};

/** The drive-time params, in the order har.com's own form lists them. Shared by
 *  the operation model (detach from the native query) and the UI converters, so
 *  the two can never disagree on which keys travel modeled. */
export const DRIVETIME_FILTER_KEYS = [
  "drivetime_address",
  "drivetime_arrival_time",
  "drivetime_travel_time",
  "drivetime_center",
] as const;

export type DriveTimeFilterKey = (typeof DRIVETIME_FILTER_KEYS)[number];

/** True when the drive-time values name somewhere to drive to. The two times
 *  alone are what har.com's form always submits; without a destination the
 *  backend rejects them and har.com ignores them, so they must not travel. */
export function hasDriveTimeDestination(
  values: Partial<Record<DriveTimeFilterKey, unknown>> | null | undefined,
): boolean {
  // A whitespace-only address is what the backend also reads as none.
  return Boolean(String(values?.drivetime_address ?? "").trim() || values?.drivetime_center);
}

/** Threads saved before CB-1509 carry the drive-time params only in the
 *  passthrough `extra` bag, which the backend no longer back-fills them from.
 *  Move them onto the modeled keys (and out of `extra`) when the filters carry
 *  no modeled destination, so "Load more" or a modal change on such a thread
 *  keeps its drive-time scope. Mutates `filters` in place. */
export function promoteLegacyDriveTime(
  filters: Partial<Record<DriveTimeFilterKey, string>> & { extra?: Record<string, string> },
): void {
  const extra = filters.extra;
  if (!extra || hasDriveTimeDestination(filters) || !hasDriveTimeDestination(extra)) return;
  const rest = { ...extra };
  for (const key of DRIVETIME_FILTER_KEYS) {
    const value = rest[key];
    delete rest[key];
    if (value != null && value !== "") filters[key] = String(value);
  }
  filters.extra = rest;
}

/** Copy the drive-time values that are set onto `target`, as strings (the
 *  modal's <select>s and the artifact both hold them that way). */
export function copyDriveTimeAsStrings(
  source: Partial<Record<DriveTimeFilterKey, unknown>> | null | undefined,
  target: Partial<Record<DriveTimeFilterKey, string>>,
): void {
  for (const key of DRIVETIME_FILTER_KEYS) {
    const value = source?.[key];
    if (value != null && value !== "") target[key] = String(value);
  }
}

export interface IBaseSearchListingsFilters {
  streetaddress?: string;
  city?: string;
  /** A single id from `restructure_filters` since the tool took exactly one; a
   *  list on chats saved before that, and on the filters the UI builds. Read it
   *  through `toCommunityField`, never by indexing (CB-1799). */
  community?: SingleOrArray<ILabeledField<number>>;
  fips_code?: ILabeledField<number>;
  subdivisions?: string;
  zip_code?: string[];
  marketarea?: string;
  mlsnum?: string;

  //bedrooms_beds?: ISearchListingsRange;
  bedroom_min?: number;
  bedroom_max?: number;

  //baths_bathrooms?: ISearchListingsRange;
  full_bath_min?: number;
  full_bath_max?: number;

  //price?: ISearchListingsRange;
  listing_price_min?: number;
  listing_price_max?: number;
  lease_price_min?: number;
  lease_price_max?: number;

  //monthly_payment?: ISearchListingsRange;
  mp_insurance_rate?: number;
  mp_mortgage_rate?: number;
  mp_down_payment_rate?: number;
  mp_term_years?: number;
  mp_price_min?: number;
  mp_price_max?: number;

  sold?: boolean;
  soldperiod?: number;
  sold_price_min?: number;
  sold_price_max?: number;
  soldfrom?: string;
  soldto?: string;
  sold_price_sqft_min?: number;
  sold_price_sqft_max?: number;
  pendingdatefrom?: string;
  pedingdateto?: string;

  for_sale?: number;
  sort?: string;
  start?: number;
  dom_min?: number;
  dom_max?: number;

  half_bath_num?: number;
  lotsize_min?: number;
  lotsize_max?: number;
  acres_min?: number;
  acres_max?: number;
  square_feet_min?: number;
  square_feet_max?: number;
  garage_num?: number;
  garage_desc?: string;
  stories?: string[];

  new_constr?: string;
  parking?: number;
  //property_type?: string[];
  /** Property-class ids. The tool artifact sends these comma-separated
   *  ("1,2" — `map_property_types_to_ids` joins them), while the native and UI
   *  paths build an array, so both containers reach the converters. */
  property_class_id?: string | string[];
  year_built_min?: number;
  year_built_max?: number;
  style?: string;
  finance?: string;
  all_status?: string;

  //price_sqft?: ISearchListingsRange;
  price_sqft_min?: number;
  price_sqft_max?: number;
  hoa_fee_max?: number;
  tags?: string;

  // Quick-access flags (set to `1` / `"Yes"` by the tool)
  pricereduced?: number;
  listed_today?: number;
  new_entry?: number;
  stype?: number;
  forcl?: number;
  new_constr2?: string;
  open_houses?: number;
  voh_only?: number;
  home_only?: number;

  school_district?: ILabeledField<string>;
  schoolelementary?: ILabeledField<string>;
  schoolmiddle?: ILabeledField<string>;
  schoolhigh?: ILabeledField<string>;
  schoolid?: ILabeledField<number>;
  nid?: ILabeledField<number>;
  neighborhood_id?: number;
  listing_agentid?: ILabeledField<string>;
  /** Rental property-type codes, comma-separated ("SGL,THC"). HAR.com sends this
   *  INSTEAD of `property_class_id` on for-rent searches, and
   *  `search_listings_tool` clears `property_class_id` when it sets this. Both
   *  ingress paths normalize it to canonical names via `mapPropTypeCodesToNames`. */
  prop_type?: string;

  map_tools_nwlat?: number;
  map_tools_nwlng?: number;
  map_tools_selat?: number;
  map_tools_selng?: number;
  map_tools_polygon?: string;
  latitude?: number;
  longitude?: number;
  radius?: number;

  /** Drive-time search, as `restructure_filters` emits it: the destination as
   *  typed, its geocoded "lat,lng", the arrival time ("HH:MM") and the travel
   *  time in minutes — a string on the wire, like every HAR select value. */
  drivetime_address?: string;
  drivetime_arrival_time?: string;
  drivetime_travel_time?: string;
  drivetime_center?: string;
}

export namespace IBaseSearchListingsFilters
{
  // Converts all types to strings
  export type QueryString = {
    [key in keyof IBaseSearchListingsFilters]?: string;
  }
}

/**
 * Interface for search listings filters — mirrors the object returned by
 * `restructure_filters` in `search_listings_tool.py` (i.e. the non-`None`
 * fields of the `SearchListingFilters` dataclass, with the amenity booleans
 * collapsed into the `amenities` array).
 */
export interface ISearchListingsFilters extends IBaseSearchListingsFilters {
  // Collapsed amenity flags (see `restructure_filters`)
  amenities?: ToolFilterAmenities[];
  max?: number;
  extra?: Record<string, string>;
  /**
   * Stamped by `restructure_filters` on every artifact written after CB-1788,
   * to say its values are raw rather than percent-encoded. Absent on older
   * ones, which is how they are recognised — see `isLegacyEncodedArtifact`.
   */
  filters_encoding?: "raw";
}

export namespace ISearchListingsFilters {
  /**
   * Older tool versions nested the half-bath flag inside an object with a
   * `half` key (e.g. `{ half: 1 }`) instead of sending a scalar.
   */
  export interface LegacyHalfBath {
    half?: number | boolean;
  }

  /**
   * Filter keys emitted by previous tool versions (and by the query-string /
   * MLS-card pre-processing paths) that `restructure_filters` no longer
   * returns. They are consumed exclusively by `transformApiFiltersLegacy`.
   */
  export interface Legacy {
    /** City display value (older MLS-card path). */
    city?: string[];
    /** ZIP codes as a comma-separated string (current output uses `string[]`). */
    zip_code?: string;
    /** Stories as a comma-separated string (current output uses `string[]`). */
    stories?: string;
    /** Subdivisions as an array (current output uses a single string). */
    subdivisions?: string[];
    /** Property-type key from older tool versions (renamed to `property_class_id`). */
    property_type?: string[];
    /** Pre-processed neighborhood display value (older MLS-card path). */
    neighborhood?: string;
    /** Legacy status key; retained but no longer mapped to a UI filter. */
    property_status?: string;
    /** Quick-access category values from the query-string path. */
    Category?: QuickAccess[];
    /** Quick-access category values from older tool versions. */
    quick_access?: QuickAccess[];
    /** County display value (newer output uses `fips_code`). */
    county?: string | string[];
    /** Tool-input county key. May arrive as a raw FIPS int, a numeric string or
     *  an `ILabeledField`; current tool output uses `fips_code` instead. */
    county_id?: number | string | ILabeledField<number>;
    /** MLS number as array/string (newer output uses `mlsnum`). */
    mls_number?: string | string[];
    /** Older tool versions nested half-bath info inside the baths object. */
    baths_bathrooms?: {
      half_bath_num?: number | boolean;
      HalfbathAvailability?: number | boolean;
    };
    /** Legacy half-bath scalar or `{ half }` object. */
    HalfbathAvailability?: number | boolean | LegacyHalfBath;
    /** Legacy value for 'all_status' filter. */
    availability?: string[];
  }

  export type Raw = RawFilters<ISearchListingsFilters>;
  export namespace Raw
  {
    export type WithLegacy = UnionIntersect<Raw, Legacy>;  
  }

  export type WithLegacy = UnionIntersect<ISearchListingsFilters, Legacy>;
}

/**
 * Interface for the HAR.com search filters
 */
export interface IHARSearchListingsFilters extends IBaseSearchListingsFilters.QueryString, IHarAmenities {
  ptids: string; // comma-separated list of subdivision IDs
};
